<script setup lang="ts">
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@zerohuman/ui'
import { computed } from 'vue'
import { API_TOKEN_RESOURCES, CONNECTION_ONLY_RESOURCES, PERSON_ONLY_SCOPES } from '@/api'
import type { ScopeLevel } from '@/composables/useScopeLevels'

/**
 * The none / read / write choice per base resource that a token is scoped with, shared so that inviting a
 * person (#619) and approving a connection at consent (#1252) offer the very control the API tokens screen does
 * rather than a second one that could drift from it. Every resource the product has is listed; the level chosen
 * for each is written straight into the bound record.
 *
 * Each choice is the kit Select (#1224): its look, focus ring and keyboard handling come from @zerohuman/ui, and
 * the `${testidPrefix}${resource}` handle sits on the native field the kit Select mirrors its value on.
 *
 * A screen may word the levels, name each control and label each row its own way (consent says "Can't see it", asks
 * "what the app may do" and describes each resource in words). That is presentation only: the values, their order and
 * the handles are the picker's.
 */
const props = defineProps<{
    testidPrefix: string
    /** What each level says in the list, where a screen words it its own way. The level's own name otherwise. */
    levelLabels?: Partial<Record<ScopeLevel, string>>
    /** The accessible name of a resource's control. `Level for <resource>` otherwise. */
    nameFor?: (resource: string) => string
    /**
     * What a resource's row says beside its control, in a screen's own words, instead of the resource id (consent
     * describes each one). Shown with its first letter capitalised. The id in monospace otherwise.
     */
    labelFor?: (resource: string) => string
    /** Granting an API token or a connected app, not a person: scopes only a person may hold are not offered. */
    credential?: boolean
    /**
     * Approving a connected app (#883): the one grant that also offers the person's own resources (`mail`), since the
     * app acts as them. A person's grant and an API token never name those, so every other screen leaves them out.
     */
    connection?: boolean
}>()
const levels = defineModel<Record<string, ScopeLevel>>({ required: true })

/** Narrowest first, the order every screen offers them in. */
const LEVELS: readonly ScopeLevel[] = ['none', 'read', 'write']

/** The resources this screen offers: every one for a connection, and all but the person's own otherwise. */
const resources = computed<readonly string[]>(() =>
    props.connection
        ? API_TOKEN_RESOURCES
        : API_TOKEN_RESOURCES.filter(resource => !CONNECTION_ONLY_RESOURCES.includes(resource)),
)

/** The levels `resource` offers: every one, less those a credential may not hold (billing:write, os#1615). */
function levelsFor(resource: string): readonly ScopeLevel[] {
    return props.credential ? LEVELS.filter(level => !PERSON_ONLY_SCOPES.includes(`${resource}:${level}`)) : LEVELS
}

function labelOf(level: ScopeLevel): string {
    return props.levelLabels?.[level] ?? level
}

function nameOf(resource: string): string {
    return props.nameFor ? props.nameFor(resource) : `Level for ${resource}`
}

/** A row's words where the screen gives them, starting a label of its own and so capitalised. */
function rowLabel(labelFor: (resource: string) => string, resource: string): string {
    const words = labelFor(resource)
    return words.charAt(0).toUpperCase() + words.slice(1)
}
</script>

<template>
	<div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
		<div v-for="resource in resources" :key="resource" class="flex items-center gap-2 text-sm">
			<Select
				:model-value="levels[resource]"
				:data-testid="`${testidPrefix}${resource}`"
				@update:model-value="levels[resource] = $event as ScopeLevel"
			>
				<SelectTrigger size="sm" :aria-label="nameOf(resource)">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem v-for="level in levelsFor(resource)":key="level" :value="level">{{ labelOf(level) }}</SelectItem>
				</SelectContent>
			</Select>
			<span v-if="labelFor">{{ rowLabel(labelFor, resource) }}</span>
			<span v-else class="font-mono text-xs">{{ resource }}</span>
		</div>
	</div>
</template>
